import * as React from 'react';
import {Form, Field} from 'react-final-form';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {User} from 'interfaces';
import {requiredValidation, minLengthValidation} from 'utilities';
import {Flex, Text, TextArea, Link, Banner, Button, NOOP} from 'components/UIKit';
import {ListingShop, ContactTrackingData} from '../../types';
import * as locales from './locales';
import {contactSeller} from './utilities';
import styles from './Contact.scss';

const TOS_LINK = '/legal/terms';
const VIEW_MORE_LINK = '/shops';

const FIELDS = {
  message: 'message',
  finances: 'finances',
  subscribe: 'subscribe',
  introduction: 'introduction',
};

export interface ContactFormProps {
  authenticityToken: string;
  show?: boolean;
  user?: User;
  shop: ListingShop;
  showCancel?: boolean;
  onCancel?: React.MouseEventHandler;
  onSuccess?: () => any;
  isOwnStore: boolean;
  contactTracking: ContactTrackingData;
}

export interface ContactFormState {
  submitted: boolean;
  submitting: boolean;
  hasSubmitError: boolean;
}

class ContactFormComponent extends React.Component<ContactFormProps & WithI18nProps, ContactFormState> {
  state: ContactFormState = {
    submitted: false,
    submitting: false,
    hasSubmitError: false,
  };

  handleSubmit = async (formValues) => {
    const {onSuccess = NOOP, isOwnStore, contactTracking, authenticityToken} = this.props;

    if (isOwnStore) {
      this.setState({
        submitted: false,
        hasSubmitError: true,
      });
      return;
    }

    this.setState({submitting: true});

    const success = await contactSeller({authenticityToken, formValues, contactTracking});

    if (success) {
      onSuccess();
    }

    this.setState({
      submitted: Boolean(success),
      submitting: false,
      hasSubmitError: !success,
    });
  };

  render() {
    const {submitted, submitting, hasSubmitError} = this.state;
    const {show = true, showCancel = true, shop, i18n, isOwnStore, onCancel = NOOP} = this.props;

    const showSuccess = submitted;
    const showForm = Boolean(show && !submitted);

    // @TODO: This is indefinitely removed until we decide on approach re: how often this is filled out/sent
    const showAboutYou = false;

    return (
      <section className={styles.FormSection}>
        {hasSubmitError && (
          <Banner richIcon="error" display="error">
            {isOwnStore ? i18n.translate('ownStore') : i18n.translate('unsuccessfulSubmit')}
          </Banner>
        )}
        {showForm && (
          <Form onSubmit={this.handleSubmit}>
            {({handleSubmit}) => (
              <form onSubmit={handleSubmit} className={styles.Form}>
                <div className={styles.InputWrap}>
                  <Field name={FIELDS.message} validate={requiredValidation}>
                    {({input, meta}) => (
                      <TextArea
                        rows={5}
                        label={i18n.translate('yourMessage')}
                        touched={meta.touched}
                        error={meta.error}
                        placeholder={i18n.translate('yourMessagePlaceholder')}
                        {...input}
                      />
                    )}
                  </Field>
                  {showAboutYou && (
                    <Field name={FIELDS.introduction} validate={minLengthValidation}>
                      {({input, meta}) => (
                        <TextArea
                          rows={5}
                          error={meta.error}
                          label={i18n.translate('aboutYou')}
                          touched={meta.touched}
                          placeholder={i18n.translate('aboutYouPlaceholder')}
                          {...input}
                        />
                      )}
                    </Field>
                  )}
                </div>
                <Flex className={styles.Footer}>
                  <Button
                    type="submit"
                    label={i18n.translate('contactSeller')}
                    disabled={submitting}
                    className={styles.ContactButton}
                  />
                  {showCancel && (
                    <Button
                      cancel
                      label={i18n.translate('cancel')}
                      onClick={onCancel}
                      className={styles.CancelButton}
                    />
                  )}
                </Flex>
                <Text text={i18n.translate('byContacting')} />
                <Link
                  newTab
                  className={styles.TosLink}
                  url={TOS_LINK}
                  text={i18n.translate('tosLink')}
                />
              </form>
            )}
          </Form>
        )}
        {showSuccess && (
          <Flex direction="column" className={styles.Success}>
            <Flex direction="column" className={styles.SuccessMessage}>
              <Text text={i18n.translate('successHeader')} />
              <Text
                text={i18n.translate('successMessage', {
                  owner: shop.isPrivate ? i18n.translate('theSeller') : shop.sellerInformation.name,
                })}
              />
            </Flex>
            <Button
              url={VIEW_MORE_LINK}
              className={styles.ViewMoreButton}
              label={i18n.translate('viewMoreBusinesses')}
            />
          </Flex>
        )}
      </section>
    );
  }
}

export const ContactForm = withI18n({
  fallback: locales.en,
  id: 'ListingContact',
})(ContactFormComponent);

export default ContactForm;
